import { Dispatch, SetStateAction, useCallback, useMemo } from "react";
import { Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { boolean, object } from "yup";
import { Checkbox } from "../../../../components/forms/checkbox";
import { RadioGroup } from "../../../../components/forms/radio";
import { useForm } from "../../../../components/hooks/form/form";
import { useAppLoader } from "../../../../components/loader/app-loader-hook";
import { AppStore } from "../../../../store";
import { CryptoDTO } from "../../../../store/crypto/models/dto";
import {
  FundsTypeEnum,
  TokenLifecycleEnum,
} from "../../../../store/crypto/models/internal";
import { CryptoService } from "../../../../store/crypto/service";
import { mapObject } from "../../../../utils/utils";
import { Container, StyledWidget } from "./components";
import { useStepValidation } from "./step";

const { defaults } = CryptoDTO.Requests.CreateFraudReport;

export const Terms: React.FunctionComponent<{
  setReportId: Dispatch<
    SetStateAction<CryptoDTO.Responses.GetSingleFraudReport.T["id"] | undefined>
  >;
}> = ({ setReportId }) => {
  const { t } = useTranslation("fields");

  const { trigger, values, errors, handleChange } = useForm<{
    acceptTerms: boolean;
  }>({
    defaultValues: useMemo(() => ({ acceptTerms: false }), []),
    validationSchema: useMemo(
      () =>
        object()
          .shape({
            acceptTerms: boolean()
              .isTrue(t("the-terms-and-conditions-must-be-accepted"))
              .required(t("the-terms-and-conditions-must-be-accepted")),
          })
          .required(),
      [t]
    ),
  });

  const loader = useAppLoader();

  const formData = useSelector<AppStore, AppStore["crypto"]["newReportForm"]>(
    (store) => store.crypto.newReportForm
  );

  const postValidation = useCallback(async () => {
    const valid = await trigger();
    if (!valid) {
      throw new Error(t("form-invalid", { ns: "common" }));
    }
    return await loader(
      CryptoService.postFraudReport({
        ...defaults,
        ...formData,
      }).then(({ id }) => {
        setReportId(id);
      })
    );
  }, [loader, formData, setReportId, trigger, t]);

  const { control } = useStepValidation(
    ["tokenLifecycle", "fundsType"],
    {},
    postValidation
  );

  return (
    <Container>
      <StyledWidget title={t("token-lifecycle-at-time-of-fraud")}>
        <Controller
          control={control}
          name="tokenLifecycle"
          defaultValue={defaults.tokenLifecycle}
          render={({ field }) => (
            <RadioGroup<typeof TokenLifecycleEnum.T>
              options={mapObject(TokenLifecycleEnum.enum, (val, key) => ({
                id: key,
                label: val,
              }))}
              {...field}
            />
          )}
        />
      </StyledWidget>
      <StyledWidget title={t("were-the-funds")}>
        <Controller
          control={control}
          name="fundsType"
          defaultValue={defaults.fundsType}
          render={({ field }) => (
            <RadioGroup<typeof FundsTypeEnum.T>
              options={mapObject(FundsTypeEnum.enum, (val, key) => ({
                id: key,
                label: val,
              }))}
              {...field}
            />
          )}
        />
      </StyledWidget>
      <Checkbox
        value={values.acceptTerms}
        checked={values.acceptTerms}
        onChange={() => handleChange("acceptTerms")(!values.acceptTerms)}
        label={t("tick-here-to-indicate-that-you")}
        error={!!errors.acceptTerms}
        helperText={errors.acceptTerms?.message}
      />
    </Container>
  );
};
